import React, { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import { FaHome } from "react-icons/fa";
import Chart from "chart.js/auto";
import "leaflet/dist/leaflet.css";
import './Mapfunction.css';
import { MapHomeControl, MapInfoControl } from "../../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const Mapfunction = () => {
  const [geoData, setGeoData] = useState(null);
  const [subGeoData, setSubGeoData] = useState(null);
  const [activeMunicipality, setActiveMunicipality] = useState(null);
  const [activeSubMunicipality, setActiveSubMunicipality] = useState(null);
  const [hoveredMunicipality, setHoveredMunicipality] = useState(null);
  const chartRef = useRef(null);

  const initializeDefaultData = useCallback(async () => {
    try {
      const [municipalityResponse, subMunicipalityResponse, municipalitiesJSON] = await Promise.all([
        fetch("/DATA/OSMbased_adm1_boundary.json"),
        fetch("/DATA/OSMbased_adm2_boundary.json"),
        fetch(`${backendUrl}roadaccident/municipality_accident_stats/`)
      ]);

      const municipalityData = await municipalityResponse.json();
      const subMunicipalityData = await subMunicipalityResponse.json();
      const municipalityStats = await municipalitiesJSON.json();

      setGeoData(municipalityData);
      setSubGeoData(subMunicipalityData);
      updateChart(municipalityStats, "Municipality");
    } catch (error) {
      console.error("Error initializing default data:", error);
    }
  }, []);

  const updateChart = (data, xAxisLabel) => {
    const labels = data.map((item) => item[xAxisLabel]);
    const totalAccidents = data.map((item) => item.TotalAccident || item.TotalAccidents);
    const injured = data.map((item) => item.Injured);
    const fatal = data.map((item) => item.Fatal);

    const chartConfig = {
      type: "bar",
      data: {
        labels,
        datasets: [
          { label: "Total Accidents", data: totalAccidents, backgroundColor: "blue" },
          { label: "Injured", data: injured, backgroundColor: "green" },
          { label: "Fatal", data: fatal, backgroundColor: "red" },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          title: { display: true, text: xAxisLabel },
        },
        scales: {
          x: {
            title: { display: true, text: xAxisLabel },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 },
          },
          y: { beginAtZero: true },
        },
      },
    };

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = document.getElementById("accidentChart").getContext("2d");
    chartRef.current = new Chart(ctx, chartConfig);
  };

  useEffect(() => {
    initializeDefaultData();
  }, [initializeDefaultData]);

  const onEachMunicipalityFeature = (feature, layer) => {
    layer.on({
      click: async () => {
        setActiveMunicipality(feature);

        if (subGeoData) {
          const filteredSubMunicipalities = subGeoData.features.filter(
            (subFeature) =>
              subFeature.properties.ADM_Code.startsWith(feature.properties.ADM_Code)
          );

          setActiveSubMunicipality(filteredSubMunicipalities);

          const municipalityName = feature.properties?.ADM1 || "";
          const subMunicipalityStatsResponse = await fetch(
            `${backendUrl}roadaccident/submunicipality_accident_stats/?municipality=${encodeURIComponent(municipalityName)}`
          );
          const subMunicipalityStats = await subMunicipalityStatsResponse.json();

          updateChart(subMunicipalityStats, "Submunicipality");
        }
      },
      mouseover: () => {
        const municipalityName = feature.properties?.ADM1 || "Unknown Municipality";
        setHoveredMunicipality(`<strong>MUNICIPALITY:</strong> ${municipalityName}`);
      },
      mouseout: () => setHoveredMunicipality(null),
    });
  };

  const ZoomToFeature = ({ activeFeature }) => {
    const map = useMap();

    useEffect(() => {
      if (activeFeature && activeFeature.geometry) {
        const { coordinates, type } = activeFeature.geometry;

        const bounds = [];
        if (type === "Polygon") {
          coordinates[0].forEach(([lon, lat]) => bounds.push([lat, lon]));
        } else if (type === "MultiPolygon") {
          coordinates.forEach((polygon) =>
            polygon[0].forEach(([lon, lat]) => bounds.push([lat, lon]))
          );
        }

        if (bounds.length > 0) {
          map.fitBounds(bounds);
        }
      }
    }, [activeFeature, map]);

    return null;
  };

  const ResetMapView = () => {
    const map = useMap();

    const resetView = async () => {
      setActiveMunicipality(null);
      setActiveSubMunicipality(null);
      setHoveredMunicipality(null);
      await initializeDefaultData();
      map.setView([-8.874217, 125.727539], 8);
    };

    return (
      <button
        onClick={resetView}
        className="reset-map-button"
        title="Reset Map and Chart"
      >
        <FaHome size={20} color="black" />
      </button>
    );
  };

  return (
    <div>
      <div className="section-2">
        <div className="section2_part1">
          <MapContainer
            className="RA_dashboard_map"
            style={{ height: "400px", width: "100%", backgroundColor: "#fff" }}
            zoom={8}
            center={[-8.874217, 125.727539]}
            zoomControl={false}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <MapHomeControl />
            <MapInfoControl text="Road accident distribution map. Click a marker or cluster to view details." />
            <ResetMapView />
            {hoveredMunicipality && (
              <div
                className="hover-text-box"
                dangerouslySetInnerHTML={{ __html: hoveredMunicipality }}
              />
            )}
            {!activeMunicipality && geoData && (
              <GeoJSON
                data={geoData}
                style={{ fillColor: "gray", color: "white", weight: 1, fillOpacity: 0.3 }}
                onEachFeature={onEachMunicipalityFeature}
              />
            )}
            {activeMunicipality && activeSubMunicipality && (
              <>
                <GeoJSON
                  data={{ type: "FeatureCollection", features: activeSubMunicipality }}
                  style={{ fillColor: "blue", color: "white", weight: 1, fillOpacity: 0.5 }}
                />
                <ZoomToFeature activeFeature={activeMunicipality} />
              </>
            )}
          </MapContainer>
        </div>
        <div className="section2_part2">
          <canvas id="accidentChart" style={{ maxWidth: "100%", height: "100%", backgroundColor: "white" }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Mapfunction;

